import React, { useState, useEffect, useCallback } from 'react';
import { View, ScrollView, Pressable, ActivityIndicator, Modal, TextInput, Platform } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { YStack, XStack, SizableText, Card, Button, Separator } from 'tamagui';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { Text } from '@constants/Themed';
import apis from '@api/api';
import { LinearGradient } from 'expo-linear-gradient';

interface Execution {
  _id: string;
  status: 'success' | 'error' | 'running' | 'skipped' | 'blocked';
  startedAt: string;
  finishedAt?: string;
  executionTime?: number;
  actionKey?: string;
  actionType?: string;
  triggerType?: string;
  inputData?: any;
  outputData?: any;
  error?: {
    message: string;
    stack?: string;
    code?: string;
  };
  conditionsEvaluated?: Array<{
    condition: any;
    result: boolean;
    action: string;
  }>;
  metadata?: any;
}

interface Analytics {
  total: number;
  success: number;
  errors: number;
  skipped: number;
  running: number;
  successRate: number;
  avgExecutionTime: number;
  byActionType: Record<string, { total: number; success: number; errors: number }>;
  byTriggerType: Record<string, number>;
  byDate: Record<string, { total: number; success: number; errors: number }>;
  topFailingActions: Array<{
    actionKey: string;
    actionType: string;
    count: number;
    errors: string[];
  }>;
}

interface FlowHistoryProps {
  dataId: string;
  actionId?: string;
  workflowId?: string;
  visible: boolean;
  onClose: () => void;
}

const FlowHistory: React.FC<FlowHistoryProps> = ({
  dataId,
  actionId,
  workflowId,
  visible,
  onClose
}) => {
  const theme = themeSelector();
  const [executions, setExecutions] = useState<Execution[]>([]);
  const [analytics, setAnalytics] = useState<Analytics | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedExecution, setSelectedExecution] = useState<Execution | null>(null);
  const [activeTab, setActiveTab] = useState<'list' | 'analytics'>('list');
  const [filters, setFilters] = useState({
    status: '',
    actionType: '',
    triggerType: '',
    startDate: '',
    endDate: '',
    search: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    if (visible && dataId) {
      loadHistory();
      loadAnalytics();
    }
  }, [visible, dataId, actionId, workflowId]);

  const loadHistory = useCallback(async () => {
    setLoading(true);
    try {
      let response;
      if (actionId) {
        response = await apis.getActionHistory(actionId, {
          limit: 100,
          ...filters
        });
      } else if (workflowId) {
        response = await apis.getWorkflowHistory(workflowId, {
          limit: 100,
          ...filters
        });
      } else {
        response = await apis.getFlowHistory(dataId, {
          limit: 100,
          ...filters
        });
      }

      if (response?.success) {
        setExecutions(response.executions || []);
      }
    } catch (error) {
      console.error('Error loading flow history:', error);
    } finally {
      setLoading(false);
    }
  }, [dataId, actionId, workflowId, filters]);

  const loadAnalytics = useCallback(async () => {
    if (!dataId) return;
    try {
      const response = await apis.getFlowAnalytics(dataId, {
        startDate: filters.startDate,
        endDate: filters.endDate
      });
      if (response?.success) {
        setAnalytics(response.analytics);
      }
    } catch (error) {
      console.error('Error loading analytics:', error);
    }
  }, [dataId, filters.startDate, filters.endDate]);

  const getStatusColor = (status: string) => {
    const colors = {
      success: '#10b981',
      error: '#ef4444',
      waiting: '#f59e0b',
      running: '#3b82f6',
      canceled: '#64748b',
      skipped: '#94a3b8',
      blocked: '#dc2626'
    };
    return colors[status] || '#64748b';
  };

  const formatDuration = (ms?: number) => {
    if (!ms) return 'N/A';
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}m`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  if (!visible) return null;

  const editorColors = {
    background: theme ? '#ffffff' : '#1e293b',
    surface: theme ? '#f8f9fa' : '#2d2d2d',
    border: theme ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.1)',
    text: theme ? '#333333' : '#e0e0e0',
    accent: 'rgb(90,192,234)',
    secondaryText: theme ? '#64748b' : '#94a3b8',
    cardBg: theme ? '#f1f5f9' : '#334155',
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <View
        style={{
          flex: 1,
          backgroundColor: 'rgba(0,0,0,0.5)',
          justifyContent: 'center',
          alignItems: 'center',
          padding: 20
        }}
      >
        <Card
          style={{
            width: Platform.OS === 'web' ? '90%' : '100%',
            maxWidth: 1200,
            maxHeight: '90%',
            backgroundColor: editorColors.background,
            borderRadius: 16
          }}
        >
          <YStack flex={1} padding="$4">
            {/* Header */}
            <XStack alignItems="center" justifyContent="space-between" marginBottom="$4">
              <SizableText fontSize="$7" fontWeight="700" color={editorColors.text}>
                Flow History
              </SizableText>
              <Pressable onPress={onClose}>
                <MaterialIcons name="close" size={24} color={editorColors.text} />
              </Pressable>
            </XStack>

            {/* Tabs */}
            <XStack gap="$2" marginBottom="$4">
              <Button
                backgroundColor={activeTab === 'list' ? editorColors.accent : editorColors.cardBg}
                borderRadius={8}
                paddingHorizontal="$4"
                paddingVertical="$2"
                onPress={() => setActiveTab('list')}
              >
                <SizableText color={activeTab === 'list' ? 'white' : editorColors.text} fontWeight="600">
                  Executions
                </SizableText>
              </Button>
              <Button
                backgroundColor={activeTab === 'analytics' ? editorColors.accent : editorColors.cardBg}
                borderRadius={8}
                paddingHorizontal="$4"
                paddingVertical="$2"
                onPress={() => setActiveTab('analytics')}
              >
                <SizableText color={activeTab === 'analytics' ? 'white' : editorColors.text} fontWeight="600">
                  Analytics
                </SizableText>
              </Button>
            </XStack>

            {/* Filters */}
            <XStack gap="$2" marginBottom="$4" flexWrap="wrap">
              <Pressable
                onPress={() => setShowFilters(!showFilters)}
                style={{
                  padding: 8,
                  borderRadius: 8,
                  backgroundColor: editorColors.cardBg,
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 4
                }}
              >
                <MaterialIcons name="filter-list" size={16} color={editorColors.text} />
                <Text style={{ fontSize: 12, color: editorColors.text }}>Filters</Text>
              </Pressable>
              {showFilters && (
                <XStack gap="$2" flexWrap="wrap" flex={1}>
                  <TextInput
                    placeholder="Status"
                    value={filters.status}
                    onChangeText={(text) => setFilters({ ...filters, status: text })}
                    style={{
                      backgroundColor: editorColors.cardBg,
                      borderRadius: 8,
                      padding: 8,
                      minWidth: 100,
                      fontSize: 12,
                      color: editorColors.text
                    }}
                  />
                  <TextInput
                    placeholder="Search"
                    value={filters.search}
                    onChangeText={(text) => setFilters({ ...filters, search: text })}
                    style={{
                      backgroundColor: editorColors.cardBg,
                      borderRadius: 8,
                      padding: 8,
                      flex: 1,
                      fontSize: 12,
                      color: editorColors.text
                    }}
                  />
                  <Button
                    backgroundColor={editorColors.accent}
                    borderRadius={8}
                    paddingHorizontal="$3"
                    paddingVertical="$2"
                    onPress={loadHistory}
                  >
                    <SizableText color="white" fontSize="$2">Apply</SizableText>
                  </Button>
                </XStack>
              )}
            </XStack>

            {/* Content */}
            {activeTab === 'analytics' ? (
              <ScrollView style={{ flex: 1 }}>
                {analytics ? (
                  <YStack gap="$4">
                    {/* Overview Cards */}
                    <XStack gap="$3" flexWrap="wrap">
                      <Card backgroundColor={editorColors.cardBg} padding="$3" flex={1} minWidth={150}>
                        <SizableText fontSize="$2" color={editorColors.secondaryText} marginBottom="$1">
                          Total Executions
                        </SizableText>
                        <SizableText fontSize="$6" fontWeight="700" color={editorColors.text}>
                          {analytics.total}
                        </SizableText>
                      </Card>
                      <Card backgroundColor={editorColors.cardBg} padding="$3" flex={1} minWidth={150}>
                        <SizableText fontSize="$2" color={editorColors.secondaryText} marginBottom="$1">
                          Success Rate
                        </SizableText>
                        <SizableText fontSize="$6" fontWeight="700" color="#10b981">
                          {analytics.successRate}%
                        </SizableText>
                      </Card>
                      <Card backgroundColor={editorColors.cardBg} padding="$3" flex={1} minWidth={150}>
                        <SizableText fontSize="$2" color={editorColors.secondaryText} marginBottom="$1">
                          Avg Execution Time
                        </SizableText>
                        <SizableText fontSize="$6" fontWeight="700" color={editorColors.text}>
                          {formatDuration(analytics.avgExecutionTime)}
                        </SizableText>
                      </Card>
                      <Card backgroundColor={editorColors.cardBg} padding="$3" flex={1} minWidth={150}>
                        <SizableText fontSize="$2" color={editorColors.secondaryText} marginBottom="$1">
                          Errors
                        </SizableText>
                        <SizableText fontSize="$6" fontWeight="700" color="#ef4444">
                          {analytics.errors}
                        </SizableText>
                      </Card>
                    </XStack>

                    {/* By Action Type */}
                    {Object.keys(analytics.byActionType).length > 0 && (
                      <Card backgroundColor={editorColors.cardBg} padding="$3">
                        <SizableText fontSize="$5" fontWeight="600" color={editorColors.text} marginBottom="$3">
                          By Action Type
                        </SizableText>
                        <YStack gap="$2">
                          {Object.entries(analytics.byActionType).map(([type, stats]) => (
                            <XStack key={type} justifyContent="space-between" alignItems="center">
                              <SizableText color={editorColors.text} fontWeight="600">
                                {type}
                              </SizableText>
                              <XStack gap="$3">
                                <SizableText color={editorColors.secondaryText} fontSize="$2">
                                  Total: {stats.total}
                                </SizableText>
                                <SizableText color="#10b981" fontSize="$2">
                                  Success: {stats.success}
                                </SizableText>
                                <SizableText color="#ef4444" fontSize="$2">
                                  Errors: {stats.errors}
                                </SizableText>
                              </XStack>
                            </XStack>
                          ))}
                        </YStack>
                      </Card>
                    )}

                    {/* Top Failing Actions */}
                    {analytics.topFailingActions.length > 0 && (
                      <Card backgroundColor={editorColors.cardBg} padding="$3">
                        <SizableText fontSize="$5" fontWeight="600" color={editorColors.text} marginBottom="$3">
                          Top Failing Actions
                        </SizableText>
                        <YStack gap="$2">
                          {analytics.topFailingActions.map((action, index) => (
                            <XStack key={index} justifyContent="space-between" alignItems="center">
                              <YStack flex={1}>
                                <SizableText color={editorColors.text} fontWeight="600">
                                  {action.actionKey} ({action.actionType})
                                </SizableText>
                                <SizableText color={editorColors.secondaryText} fontSize="$2">
                                  {action.errors[0] || 'Unknown error'}
                                </SizableText>
                              </YStack>
                              <SizableText color="#ef4444" fontWeight="600">
                                {action.count} failures
                              </SizableText>
                            </XStack>
                          ))}
                        </YStack>
                      </Card>
                    )}
                  </YStack>
                ) : (
                  <View style={{ padding: 40, alignItems: 'center' }}>
                    <ActivityIndicator size="large" color={editorColors.accent} />
                  </View>
                )}
              </ScrollView>
            ) : (
              <ScrollView style={{ flex: 1 }}>
                {loading ? (
                  <View style={{ padding: 40, alignItems: 'center' }}>
                    <ActivityIndicator size="large" color={editorColors.accent} />
                  </View>
                ) : executions.length === 0 ? (
                  <View style={{ padding: 40, alignItems: 'center' }}>
                    <Text style={{ color: editorColors.secondaryText }}>
                      No executions yet
                    </Text>
                  </View>
                ) : (
                  <YStack gap="$3">
                    {executions.map((execution) => (
                      <Pressable
                        key={execution._id}
                        onPress={() => setSelectedExecution(
                          selectedExecution?._id === execution._id ? null : execution
                        )}
                      >
                        <Card
                          style={{
                            backgroundColor: editorColors.cardBg,
                            borderWidth: 1,
                            borderColor: editorColors.border
                          }}
                        >
                          <YStack padding="$3">
                            <XStack alignItems="center" justifyContent="space-between" marginBottom="$2">
                              <XStack alignItems="center" gap="$2">
                                <View
                                  style={{
                                    width: 12,
                                    height: 12,
                                    borderRadius: 6,
                                    backgroundColor: getStatusColor(execution.status)
                                  }}
                                />
                                <Text style={{ fontWeight: '600', color: editorColors.text }}>
                                  {execution.status.toUpperCase()}
                                </Text>
                                {execution.actionKey && (
                                  <Text style={{ fontSize: 12, color: editorColors.secondaryText }}>
                                    {execution.actionKey} ({execution.actionType})
                                  </Text>
                                )}
                              </XStack>
                              <Text style={{ fontSize: 12, color: editorColors.secondaryText }}>
                                {formatDate(execution.startedAt)}
                              </Text>
                            </XStack>

                            <XStack gap="$4" marginTop="$2">
                              {execution.executionTime !== undefined && (
                                <Text style={{ fontSize: 12, color: editorColors.secondaryText }}>
                                  Duration: {formatDuration(execution.executionTime)}
                                </Text>
                              )}
                              {execution.triggerType && (
                                <Text style={{ fontSize: 12, color: editorColors.secondaryText }}>
                                  Trigger: {execution.triggerType}
                                </Text>
                              )}
                            </XStack>

                            {execution.error && (
                              <View
                                style={{
                                  marginTop: 8,
                                  padding: 8,
                                  backgroundColor: theme ? '#fee2e2' : '#7f1d1d',
                                  borderRadius: 6
                                }}
                              >
                                <Text style={{ fontSize: 12, color: theme ? '#dc2626' : '#fca5a5' }}>
                                  {execution.error.message}
                                </Text>
                              </View>
                            )}

                            {selectedExecution?._id === execution._id && (
                              <View style={{ marginTop: 12, padding: 12, backgroundColor: editorColors.background, borderRadius: 8 }}>
                                <SizableText fontSize="$3" fontWeight="600" marginBottom="$2" color={editorColors.text}>
                                  Execution Details
                                </SizableText>
                                {execution.inputData && (
                                  <YStack marginBottom="$2">
                                    <SizableText fontSize="$2" color={editorColors.secondaryText} marginBottom="$1">
                                      Input Data:
                                    </SizableText>
                                    <Text style={{ fontSize: 11, color: editorColors.text, fontFamily: 'monospace' }}>
                                      {JSON.stringify(execution.inputData, null, 2)}
                                    </Text>
                                  </YStack>
                                )}
                                {execution.outputData && (
                                  <YStack marginBottom="$2">
                                    <SizableText fontSize="$2" color={editorColors.secondaryText} marginBottom="$1">
                                      Output Data:
                                    </SizableText>
                                    <Text style={{ fontSize: 11, color: editorColors.text, fontFamily: 'monospace' }}>
                                      {JSON.stringify(execution.outputData, null, 2)}
                                    </Text>
                                  </YStack>
                                )}
                                {execution.conditionsEvaluated && execution.conditionsEvaluated.length > 0 && (
                                  <YStack marginBottom="$2">
                                    <SizableText fontSize="$2" color={editorColors.secondaryText} marginBottom="$1">
                                      Conditions Evaluated:
                                    </SizableText>
                                    {execution.conditionsEvaluated.map((cond, idx) => (
                                      <Text key={idx} style={{ fontSize: 11, color: cond.result ? '#10b981' : '#ef4444' }}>
                                        {idx + 1}. {cond.result ? '✓' : '✗'} - {cond.action}
                                      </Text>
                                    ))}
                                  </YStack>
                                )}
                                {execution.metadata?.agentDecisions && execution.metadata.agentDecisions.length > 0 && (
                                  <YStack>
                                    <SizableText fontSize="$2" color={editorColors.secondaryText} marginBottom="$1">
                                      Agent Decisions:
                                    </SizableText>
                                    {execution.metadata.agentDecisions.map((decision: any, idx: number) => (
                                      <Text key={idx} style={{ fontSize: 11, color: editorColors.text }}>
                                        {JSON.stringify(decision, null, 2)}
                                      </Text>
                                    ))}
                                  </YStack>
                                )}
                              </View>
                            )}
                          </YStack>
                        </Card>
                      </Pressable>
                    ))}
                  </YStack>
                )}
              </ScrollView>
            )}
          </YStack>
        </Card>
      </View>
    </Modal>
  );
};

export default FlowHistory;



